<template>
  <div class="customer-profile p-4">
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
    </div>

    <div v-else-if="profile" class="row">
      <!-- Customer Info Card -->
      <div class="col-lg-4">
        <div class="card p-4 mb-4 text-center shadow-sm">
          <div class="mb-3">
             <div class="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" style="width: 80px; height: 80px; font-size: 2rem;">
               {{ profile.email[0].toUpperCase() }}
             </div>
          </div>
          <h4>{{ profile.email }}</h4>
          <div class="mt-2">
            <span class="badge" :class="getStatusClass(profile.status)">{{ profile.status }} Customer</span>
          </div>
          <hr>
          <div class="row text-center mt-3">
            <div class="col border-end">
              <div class="fw-bold fs-5">{{ profile.orders.length }}</div>
              <small class="text-muted">Orders</small>
            </div>
            <div class="col">
              <div class="fw-bold fs-5">{{ formatCurrency(profile.total_value) }}</div>
              <small class="text-muted">Lifetime Value</small>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="card p-3 shadow-sm">
           <h6>Quick Actions</h6>
           <div class="d-grid gap-2">
             <button class="btn btn-outline-primary btn-sm" @click="contactCustomer">
               <i class="bi bi-envelope"></i> Send Email
             </button>
             <button class="btn btn-outline-secondary btn-sm" @click="addNote">
               <i class="bi bi-journal-plus"></i> Add Admin Note
             </button>
           </div>
        </div>
      </div>

      <div class="col-lg-8">
        <!-- Order History -->
        <div class="card p-4 mb-4 shadow-sm">
          <h5 class="mb-3">Order History</h5>
          <div v-if="!profile.orders.length" class="text-muted">No orders found.</div>
          <table v-else class="table table-hover align-middle">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Date</th>
                <th>Status</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in profile.orders" :key="order.id">
                <td>
                  <router-link :to="'/orders/' + order.id" class="fw-bold text-decoration-none">
                    #{{ order.pretty_id }}
                  </router-link>
                </td>
                <td>{{ formatDate(order.created_at) }}</td>
                <td>
                  <span class="badge" :class="orderStatuses[order.status]?.color">
                    {{ orderStatuses[order.status]?.name }}
                  </span>
                </td>
                <td>{{ formatCurrency(order.total_price, order.currency_code) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Support History -->
        <div class="card p-4 shadow-sm">
          <h5 class="mb-3">Past Tickets & Interactions</h5>
          <div v-if="!profile.threads.length" class="text-muted">No support history found.</div>
          <div v-else class="list-group list-group-flush">
            <div v-for="thread in profile.threads" :key="thread.id" class="list-group-item px-0 py-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <h6 class="mb-0">
                  <router-link :to="'/messages?thread=' + thread.id" class="text-decoration-none text-dark">
                    {{ thread.subject || 'Inquiry about order' }}
                  </router-link>
                </h6>
                <small class="text-muted">{{ formatDate(thread.updated_at) }}</small>
              </div>
              <p class="small text-muted mb-2 text-truncate" v-if="thread.messages?.[0]">
                Last message: {{ thread.messages[0].text }}
              </p>
              <div class="d-flex gap-2">
                <span v-for="ticket in thread.tickets" :key="ticket.id" class="badge bg-light text-dark border">
                  Ticket #{{ ticket.id }}: {{ ticket.status }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="alert alert-danger">
      Customer profile not found.
    </div>
  </div>
</template>

<script>
import http from "@/modules/http";
import date from "@/modules/date";
import orderStatuses from "../../../modules/OrderStatuses";

export default {
  name: "CustomerProfile",
  props: ['email'],
  data() {
    return {
      loading: true,
      profile: null,
      orderStatuses: orderStatuses
    };
  },
  mounted() {
    this.loadProfile();
  },
  watch: {
    email() {
      this.loadProfile();
    }
  },
  methods: {
    loadProfile() {
      this.loading = true;
      const email = this.email || this.$route.params.email;
      http.fetch(`/customers/profile/${email}`).then(data => {
        this.profile = { ...data, email };
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      });
    },
    formatDate(val) {
      return date.format(val, true, false);
    },
    formatCurrency(val, code = 'USD') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(val);
    },
    getStatusClass(status) {
      if (status === 'VIP') return 'bg-warning text-dark';
      if (status === 'Gold') return 'bg-info text-dark';
      return 'bg-secondary';
    },
    contactCustomer() {
       window.location.href = `mailto:${this.profile.email}`;
    },
    addNote() {
       alert("Add admin note functionality would go here.");
    }
  }
}
</script>

<style scoped>
.customer-profile {
  background-color: #f8f9fa;
  min-height: 100vh;
}
.card {
  border: none;
  border-radius: 12px;
}
</style>
